import { cn } from "@/lib/utils";
import type { Vital, VitalLevel } from "./vitals";

/**
 * The vitals strip.
 *
 * A healthy therapist reads as a calm grey row, so any colour on this strip is
 * a real signal rather than decoration. Each cell carries a rule along its top
 * edge that stays invisible until the value crosses a threshold, which lets an
 * admin spot a problem therapist from the top of the page without reading a
 * single number.
 */

const VALUE_TONE: Record<VitalLevel, string> = {
  idle: "text-muted-foreground/40",
  neutral: "text-foreground",
  watch: "text-amber-700",
  alarm: "text-rose-700",
};

const RULE_TONE: Record<VitalLevel, string> = {
  idle: "bg-transparent",
  neutral: "bg-transparent",
  watch: "bg-amber-500",
  alarm: "bg-rose-500",
};

const CELL_TONE: Record<VitalLevel, string> = {
  idle: "bg-card",
  neutral: "bg-card",
  watch: "bg-amber-50/40",
  alarm: "bg-rose-50/40",
};

function VitalCell({ vital }: { vital: Vital }) {
  const flagged = vital.level === "watch" || vital.level === "alarm";

  return (
    <div className={cn("relative px-4 pb-3 pt-3.5", CELL_TONE[vital.level])}>
      <span
        aria-hidden
        className={cn(
          "absolute inset-x-0 top-0 h-[2px] transition-colors",
          RULE_TONE[vital.level]
        )}
      />
      <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">
        {vital.label}
      </p>
      <p
        className={cn(
          "mt-1.5 text-[22px] font-semibold leading-none tabular-nums",
          VALUE_TONE[vital.level]
        )}
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {vital.value}
        {flagged && <span className="sr-only"> (zahtijeva pažnju)</span>}
      </p>
      <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground/80">
        {vital.detail}
      </p>
    </div>
  );
}

export function TherapistVitals({ vitals }: { vitals: Vital[] }) {
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border bg-border shadow-sm sm:grid-cols-3 lg:grid-cols-6">
      {vitals.map((v) => (
        <VitalCell key={v.key} vital={v} />
      ))}
    </div>
  );
}

/**
 * Rating distribution.
 *
 * An average hides the shape of the feedback: 4.0 can mean "everyone is
 * content" or "most love them and a few had a bad experience". Those two cases
 * call for different admin action, so the counts are shown directly.
 */
export function RatingDistribution({
  distribution,
  total,
  average,
}: {
  distribution: number[];
  total: number;
  average: number | null;
}) {
  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground/60">
        Nijedna sesija još nije ocijenjena.
      </p>
    );
  }

  const max = Math.max(...distribution);

  return (
    <div>
      <div className="mb-3 flex items-baseline gap-2">
        <span
          className="text-2xl font-semibold leading-none tabular-nums text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {average?.toFixed(1)}
        </span>
        <span className="text-xs text-muted-foreground">
          prosjek iz {total} {total === 1 ? "ocjene" : "ocjena"}
        </span>
      </div>

      <div className="space-y-1">
        {[5, 4, 3, 2, 1].map((stars) => {
          const count = distribution[stars - 1] ?? 0;
          const low = stars <= 2;
          return (
            <div key={stars} className="flex items-center gap-2">
              <span className="w-3 text-right text-[11px] tabular-nums text-muted-foreground">
                {stars}
              </span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    "h-full rounded-full",
                    count === 0
                      ? "bg-transparent"
                      : low
                        ? "bg-rose-400"
                        : "bg-primary/60"
                  )}
                  style={{ width: max > 0 ? `${(count / max) * 100}%` : "0%" }}
                />
              </div>
              <span
                className={cn(
                  "w-5 text-right text-[11px] tabular-nums",
                  count > 0 && low ? "text-rose-600" : "text-muted-foreground"
                )}
              >
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
